import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { captureExceptionWithoutPhi } from "@/utils/sentry";

/**
 * What a section had to write when the billing tab was saved.
 *
 * `nothing-to-write` is not a failure: a tab whose sections are all up to date is the normal case, and
 * it is what lets the tab tell "there was nothing here to save" apart from "it was saved".
 */
export type BillingSectionSaveOutcome = "written" | "nothing-to-write" | "failed";

/**
 * A section's own save. It reports how it went rather than throwing, and tells the user itself — the
 * section knows what it was writing, the button only knows that something was.
 */
type BillingSectionSaveHandler = () => Promise<BillingSectionSaveOutcome>;

export interface BillingSectionSaveCoordinator {
  register: (key: symbol, handler: BillingSectionSaveHandler) => () => void;
  saveAll: () => Promise<BillingSectionSaveOutcome>;
}

const BillingSectionSaveContext = createContext<BillingSectionSaveCoordinator | null>(null);

/**
 * Lets the billing tab's Save Billing Info button write work held by the section below it.
 *
 * The button sits outside the section on purpose — both implementations of the tab body offer the
 * same actions, so neither can drift on what a biller is allowed to do here — which leaves it with no
 * way to reach what the section is holding. Sections register their save here and the button runs
 * them, the same way `EncounterChildSaveContext` connects the encounter's Save to its sections.
 */
export function useBillingSectionSaveCoordinator(): BillingSectionSaveCoordinator {
  const handlersRef = useRef(new Map<symbol, BillingSectionSaveHandler>());

  const register = useCallback((key: symbol, handler: BillingSectionSaveHandler) => {
    handlersRef.current.set(key, handler);
    return () => {
      handlersRef.current.delete(key);
    };
  }, []);

  const saveAll = useCallback(async (): Promise<BillingSectionSaveOutcome> => {
    const handlers = Array.from(handlersRef.current.values());

    if (!handlers.length) return "nothing-to-write";

    const outcomes = await Promise.all(
      handlers.map(async (handler) => {
        try {
          return await handler();
        } catch (error) {
          // A handler reports its failures rather than throwing them, so reaching here is a bug in the
          // section. Reported rather than swallowed, and the save reads as failed: the button must not
          // tell a biller their work was saved on the strength of a handler that crashed.
          captureExceptionWithoutPhi("A billing section's save threw instead of reporting", error);
          return "failed" as const;
        }
      }),
    );

    if (outcomes.includes("failed")) return "failed";

    return outcomes.includes("written") ? "written" : "nothing-to-write";
  }, []);

  return useMemo(() => ({ register, saveAll }), [register, saveAll]);
}

export function BillingSectionSaveProvider({
  coordinator,
  children,
}: {
  coordinator: BillingSectionSaveCoordinator;
  children: ReactNode;
}) {
  return (
    <BillingSectionSaveContext.Provider value={coordinator}>
      {children}
    </BillingSectionSaveContext.Provider>
  );
}

/**
 * Registers a section of the billing tab to be saved when the tab is.
 *
 * Keeps the latest render's handler, so a section always saves what it is currently holding without
 * having to memoize anything.
 */
export function useRegisterBillingSectionSave(handler: BillingSectionSaveHandler, enabled = true) {
  const coordinator = useContext(BillingSectionSaveContext);
  const keyRef = useRef(Symbol("billing-section-save"));
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!coordinator || !enabled) return;

    return coordinator.register(keyRef.current, () => handlerRef.current());
  }, [coordinator, enabled]);
}
